// SMS delivery-status presentation helpers (KPT-1036, KPT-1117, KPT-1120).
//
// One vocabulary, two column names. /sms_notifications and /all_logs return
// Twilio's `twilio_status`; /uap_logs has no status column at all, so the store
// derives `sms_status` from the boolean `if_sms_success`
// (store/notifications/sms-uap-logs.js), and the /all_logs UNION emits the same
// 'sent' | 'failed' | NULL for its UAP branch. Because 'sent' and 'failed' are
// also Twilio statuses, the buckets below classify BOTH vocabularies - which is
// what lets every Notifications tab share one badge definition.

// KPT-1117: the two buckets the SMS Status filter offers. Twilio reports seven
// lifecycle statuses but users only ask "did it go out or not", so the search
// mapper expands one dropdown choice into the matching status list. Keep these
// a partition of the Twilio vocabulary - a status in neither list becomes
// unfilterable. Rows with no status yet (NULL) match neither bucket.
// KPT-1120: these are now the BADGE's vocabulary too, so the badge and the
// filter can no longer disagree. Editing either array changes both the query
// built in store/notifications/search-inputs.js AND the badge colour and text.
export const TWILIO_SENT_STATUSES = ['queued', 'accepted', 'sending', 'sent', 'delivered']
export const TWILIO_FAILED_STATUSES = ['undelivered', 'failed']

export const TWILIO_BUCKET_SENT = 'sent'
export const TWILIO_BUCKET_FAILED = 'failed'

// KPT-1120: the one classifier. 'sent'/'failed' are both Twilio statuses AND
// the literals the UAP store derives from if_sms_success, so this single
// function serves the twilio_status column and the sms_status column alike.
// Returns null for an absent or unrecognized status - the caller decides what
// to show, rather than a fallback silently meaning "failed".
export function twilioStatusBucket (status) {
  if (TWILIO_SENT_STATUSES.includes(status)) {
    return TWILIO_BUCKET_SENT
  }
  if (TWILIO_FAILED_STATUSES.includes(status)) {
    return TWILIO_BUCKET_FAILED
  }
  return null
}

// KPT-1120: one colour per outcome, in every view, tab and filter. Deliberately
// NOT a per-status map - a per-status map is exactly what made 'sent' green on
// the UAP & Legacy Logs tab and blue on the All tab.
export function twilioStatusVariant (status) {
  const bucket = twilioStatusBucket(status)
  if (bucket === TWILIO_BUCKET_SENT) {
    return 'success'
  }
  if (bucket === TWILIO_BUCKET_FAILED) {
    return 'danger'
  }
  return 'secondary'
}

// KPT-1120: the localized badge text, so the badge reads in the user's language
// instead of echoing Twilio's raw English status.
export function twilioStatusLabel (status) {
  // Import messages lazily to avoid circular dependency: utils/strings.js
  // imports @/store, and store/notifications/search-inputs.js imports THIS file
  // at module scope, so a top-level import would close
  // store -> notifications/sms -> search-inputs -> twilio-status -> strings -> store
  // and silently break Vuex module registration. Resolving per call also means
  // the label follows profile.locale reactively, one render at a time.
  const { messages } = require('@/utils/strings')
  const bucket = twilioStatusBucket(status)
  if (bucket === TWILIO_BUCKET_SENT) {
    return messages.getLabelSent()
  }
  if (bucket === TWILIO_BUCKET_FAILED) {
    return messages.getLabelFailed()
  }
  // Pass through untouched. A null status MUST stay falsy so the badge slot's
  // `v-if="row.value"` keeps rendering a blank cell instead of a badge, and an
  // unrecognized status is shown raw rather than mislabelled as a failure.
  return status
}

// One display string out of the Twilio error code + message, null when the
// send had no error.
export function formatTwilioError (errorCode, errorMessage) {
  if (errorCode == null && !errorMessage) {
    return null
  }
  if (errorCode != null && errorMessage) {
    return `Error ${errorCode}: ${errorMessage}`
  }
  return errorMessage || `Error ${errorCode}`
}
